import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, LayoutGrid } from "lucide-react";
import { AtwProductMark } from "@/components/Brand/AtwProductMark";
import { atwMarkIdForAppSlug } from "@/components/Brand/atwProductMarks";
import { GettingStartedPanel } from "@/components/Dashboard/GettingStartedPanel";
import { apiGet } from "@/lib/apiClient";
import { fetchCrmOverviewFromLists } from "@/lib/crmAggregate";
import {
  DASHBOARD_APP_BY_SLUG,
  DASHBOARD_APP_ORDER,
  type GlobalAppSlug,
} from "@/lib/dashboardAppRegistry";

const APP_BLURBS: Partial<Record<GlobalAppSlug, string>> = {
  crm: "Leads, contacts, accounts, deals",
  finance: "Finance workspace",
  "human-resources": "Employee directory",
  marketing: "Campaigns, blogs, and socials",
  knowledge: "Articles, with optional libraries and books",
  projects: "Projects, tasks, and issues",
  worksuite: "Email, calendar, files, meetings",
  analytics: "Traffic and conversion analytics",
  "custom-apps": "Org-owned apps and records",
  support: "Tickets and AI help",
  platform: "ATW operator console",
};

type TrialPlanResponse = {
  isTrial?: boolean;
  trialDaysRemaining?: number | null;
  trialExpired?: boolean;
};

type FinanceSummary = { openTotal?: number; overdueCount?: number };

function formatMoney(n: number) {
  return new Intl.NumberFormat(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
}

export function HomePage() {
  const [trialPlan, setTrialPlan] = useState<TrialPlanResponse | null>(null);
  const [crmHint, setCrmHint] = useState<string | null>(null);
  const [financeHint, setFinanceHint] = useState<string | null>(null);

  const visibleApps = DASHBOARD_APP_ORDER.filter((slug) => slug !== "home");

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const [planRes, crmRes, finRes] = await Promise.all([
        apiGet<TrialPlanResponse>("/api/billing/me/plan"),
        fetchCrmOverviewFromLists({ includeDeals: false }),
        apiGet<FinanceSummary>("/api/finance/summary"),
      ]);
      if (cancelled) return;
      if (planRes.ok) setTrialPlan(planRes.data ?? null);
      if (crmRes.ok && crmRes.snapshot) {
        const s = crmRes.snapshot;
        setCrmHint(`${s.openLeadRecordCount} open leads · ${s.namedAccountCount} accounts`);
      }
      if (finRes.ok && finRes.data) {
        const f = finRes.data;
        setFinanceHint(`${formatMoney(f.openTotal ?? 0)} open AR · ${f.overdueCount ?? 0} overdue`);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const isTrial = trialPlan?.isTrial === true;
  const trialExpired = trialPlan?.trialExpired === true;
  const daysRemaining =
    typeof trialPlan?.trialDaysRemaining === "number" ? trialPlan.trialDaysRemaining : null;
  const trialText = !isTrial
    ? null
    : trialExpired
      ? "Your free trial has ended. Choose your plan to keep full access."
      : typeof daysRemaining === "number"
        ? `${daysRemaining} day${daysRemaining === 1 ? "" : "s"} remaining in your free trial.`
        : "Your free trial is active.";

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          {isTrial ? "Getting Started Dashboard" : "Workspace Overview"}
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          {isTrial
            ? "Start with One AI (or a first-job tile), then jump into the ATW One apps your role can access."
            : "Quick entry to your apps and a high-level view of ATW One."}
        </p>
        {trialText ? (
          <p
            className={`mt-2 text-sm font-medium ${trialExpired ? "text-rose-600 dark:text-rose-300" : "text-amber-700 dark:text-amber-200"}`}
          >
            {trialText}
          </p>
        ) : null}
      </div>

      <GettingStartedPanel isTrial={isTrial} />

      {(crmHint || financeHint) && (
        <div className="grid gap-3 sm:grid-cols-2">
          {crmHint ? (
            <Link
              to="/crm/overview"
              className="rounded-lg border border-gray-200 bg-gray-50/80 px-4 py-3 transition hover:border-indigo-200 dark:border-white/10 dark:bg-black/20"
            >
              <p className="text-xs font-semibold uppercase text-gray-500">CRM snapshot</p>
              <p className="mt-1 text-sm font-medium text-gray-900 dark:text-white">{crmHint}</p>
            </Link>
          ) : null}
          {financeHint ? (
            <Link
              to="/finance"
              className="rounded-lg border border-gray-200 bg-gray-50/80 px-4 py-3 transition hover:border-indigo-200 dark:border-white/10 dark:bg-black/20"
            >
              <p className="text-xs font-semibold uppercase text-gray-500">Finance snapshot</p>
              <p className="mt-1 text-sm font-medium text-gray-900 dark:text-white">{financeHint}</p>
            </Link>
          ) : null}
        </div>
      )}

      <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-white/5">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Global Apps</h2>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">Open any app your role can access.</p>
        <ul className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {visibleApps.map((slug) => {
            const app = DASHBOARD_APP_BY_SLUG[slug];
            if (!app) return null;
            const markId = atwMarkIdForAppSlug(slug);
            return (
              <li key={slug}>
                <Link
                  to={app.topNavHref}
                  className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50/80 px-4 py-3 transition hover:border-indigo-200 hover:bg-white dark:border-white/10 dark:bg-black/20 dark:hover:border-indigo-500/40"
                >
                  {markId ? (
                    <AtwProductMark id={markId} size={26} className="dark:brightness-0 dark:invert" />
                  ) : (
                    <LayoutGrid className="h-6 w-6 shrink-0 text-indigo-600 dark:text-indigo-400" aria-hidden />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-gray-900 dark:text-white">{app.label}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {APP_BLURBS[slug] || "ATW One module"}
                    </p>
                  </div>
                  <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" aria-hidden />
                </Link>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="flex flex-wrap gap-3 text-sm">
        <Link to="/organization" className="font-medium text-indigo-600 hover:underline dark:text-indigo-300">
          Organization admin
        </Link>
        <span className="text-gray-300 dark:text-gray-600">·</span>
        <a href="https://worksuite.atw.net/" className="font-medium text-indigo-600 hover:underline dark:text-indigo-300">
          Open WorkSuite
        </a>
      </div>
    </div>
  );
}
